/**
 * TaskFormModal — shared create/edit dialog for admin tasks.
 *
 * Extracted from AdminTasksPage.jsx (T2.1.1) so the upcoming
 * AdminCalendarPage (T2.1.2+) can reuse the same modal without
 * duplicating fields, validation, or payload shaping.
 *
 * No behaviour change vs the previous inline component — same props,
 * same internal state, same validation, same JSX, same colours.
 *
 * Props:
 *   open          boolean
 *   mode          "create" | "edit"
 *   task          object | null     (the row to edit; null for create)
 *   onOpenChange  (next: boolean) => void
 *   onSaved       () => void        (called after a successful save)
 *
 * Named exports ``TASK_TYPE_OPTIONS`` / ``PRIORITY_OPTIONS`` are the
 * single source of truth for both this modal's `<select>` lists and the
 * AdminTasksPage filter bar (the derived label maps live next to the
 * page-level pills and stay in AdminTasksPage.jsx).
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { createAdminTask, updateAdminTask } from "../../../api/adminData";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import MultiEntityLinkFields from "../entityLinks/MultiEntityLinkFields";
import UserPicker from "../users/UserPicker";
import UserMultiPicker from "../users/UserMultiPicker";
import useResponsibilityAreas from "../responsibilities/useResponsibilityAreas";
import ActivityCategorySelect from "../activityCategories/ActivityCategorySelect";
import { activityCategoryErrorMessage } from "../activityCategories/activityCategoryErrors";
import { invalidateActivityCategoryCatalog } from "../activityCategories/activityCategoryCatalogStore";
import {
  hasUsableDefaults,
  resolveCategoryChange,
  suggestionPeople,
} from "../responsibilities/suggestionRules";
import {
  EMPTY_FORM,
  formToPayload,
  taskToForm,
} from "./taskFormState";


// ──────────────────────────────────────────────────────────────────────
// Option lists — keep in sync with backend Literals in
// app/schemas/task.py and the CHECK constraints in Migration 108.
// ──────────────────────────────────────────────────────────────────────

// Single source of truth for the task categories: AdminTasksPage,
// AdminTaskDetailPage, EntityTasksTab and CalendarFilters all derive
// their labels and filter options from this array.
//
// Slice B additions are ADDITIVE — no existing value renamed or removed.
// Values that read similarly are distinct on purpose:
//   tenant_issue     Mieter-Problem — disruption / complaint / escalation
//   tenant_care      Mieterbetreuung — ongoing support and communication
//   document_request Dokument-Anfrage — ASKING someone for a document
//   documentation    Dokumentation — writing internal documentation
// There is no automatic mapping between the old and the new values.
export const TASK_TYPE_OPTIONS = [
  { value: "general", label: "Allgemein" },
  { value: "viewing", label: "Besichtigung" },
  { value: "handover", label: "Übergabe" },
  { value: "contract", label: "Verträge" },
  { value: "applicant_followup", label: "Bewerber-Nachfass" },
  { value: "tenant_issue", label: "Mieter-Problem" },
  { value: "maintenance", label: "Wartung" },
  { value: "document_request", label: "Dokument-Anfrage" },
  { value: "reminder", label: "Erinnerung" },
  { value: "meeting", label: "Meeting" },
  // — Slice B —
  { value: "tenant_care", label: "Mieterbetreuung" },
  { value: "inventory", label: "Inventar" },
  { value: "crm", label: "CRM" },
  { value: "documentation", label: "Dokumentation" },
  { value: "inspection", label: "Kontrolle" },
  { value: "administration", label: "Administration" },
  { value: "bookkeeping", label: "Buchhaltung" },
];

export const PRIORITY_OPTIONS = [
  { value: "low", label: "Niedrig" },
  { value: "medium", label: "Mittel" },
  { value: "high", label: "Hoch" },
  { value: "urgent", label: "Dringend" },
];



// ──────────────────────────────────────────────────────────────────────
// Modal component
// ──────────────────────────────────────────────────────────────────────

export default function TaskFormModal({
  open,
  mode,
  task,
  onOpenChange,
  onSaved,
  initialFormData = null,
}) {
  const isEdit = mode === "edit" && !!task;
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  /**
   * RESP1 dirty guard. Once the user has decided anything about who is
   * on this task — named someone, removed someone, or deliberately left
   * it empty — no configuration may overwrite that. Only ``true`` is
   * ever set by a human action; applying a suggestion does not.
   */
  const [peopleTouched, setPeopleTouched] = useState(false);
  /** Which category the people currently in the form were suggested by,
   * or ``null`` if they were not suggested at all. Lets a category
   * change withdraw its own proposal without touching manual input. */
  const [suggestedFrom, setSuggestedFrom] = useState(null);
  // Loaded only while CREATING: an existing task shows what was saved,
  // and re-suggesting into it would rewrite history. A failure here is
  // not fatal — no defaults simply means no defaults.
  const { byCategory: responsibilityByCategory } = useResponsibilityAreas({
    enabled: open && !isEdit,
  });
  // D4b: the five bulk entity lists this modal used to pull on every
  // open are gone. They existed only to give the old EntityCombobox a
  // label for an already-selected id, and they were unbounded in
  // practice — fetchAdminUnits() sent no parameters at all, so unit 51
  // was unreachable. Every link is now an async picker that asks the
  // server for one page at a time, and D4a3 supplies the selected row's
  // name on the task itself, so opening the form costs nothing.

  useEffect(() => {
    if (open) {
      if (isEdit) {
        setForm(taskToForm(task));
      } else {
        // T2.1.3: in create mode, merge any caller-supplied prefill
        // (e.g. due_at from a calendar dateClick) over the empty form.
        // Values must match the form's local-input shape — e.g.
        // due_at as "YYYY-MM-DDTHH:mm" (datetime-local string).
        setForm({ ...EMPTY_FORM, ...(initialFormData || {}) });
      }
      setSubmitting(false);
      // RESP1: each opening starts a fresh suggestion cycle. A caller
      // that prefilled people counts as a manual decision — a default
      // must not overwrite what the opener deliberately set.
      setPeopleTouched(
        Boolean(
          initialFormData?.assigned_to_user_id ||
            (initialFormData?.participant_user_ids || []).length
        )
      );
      setSuggestedFrom(null);
    }
  }, [open, isEdit, task, initialFormData]);

  function handleClose(next) {
    if (!next && submitting) return;
    onOpenChange(next);
  }

  function setField(name, value) {
    setForm((s) => ({ ...s, [name]: value }));
  }

  const setIds = useCallback((field, next) => {
    setForm((s2) => ({ ...s2, [field]: next }));
    // RESP1: touching the people fields is a decision. Adding, removing
    // and clearing all count — "I deliberately left this empty" has to
    // survive a later category change just as a name does.
    if (field === "participant_user_ids") setPeopleTouched(true);
  }, []);

  /**
   * ASSIGN1: promoting a participant to responsible drops their
   * participant entry in the same state update.
   *
   * One person holds one role, and the server enforces that on write.
   * Doing it here too is not belt-and-braces for its own sake: without
   * it the chip stays on screen until the response comes back and then
   * disappears, which reads as the save having removed someone the user
   * did not touch.
   */
  const setAssignee = useCallback((id) => {
    setForm((s2) => ({
      ...s2,
      assigned_to_user_id: id || "",
      participant_user_ids: (s2.participant_user_ids || []).filter(
        (p) => String(p) !== String(id)
      ),
    }));
    setPeopleTouched(true);
  }, []);

  /** RESP1: write a category's configured people into the form.
   *
   * Called only from the category handler and from the explicit
   * "Standard übernehmen" button, never from an effect — a suggestion
   * that arrives on its own schedule is exactly the race that would
   * overwrite something the user just typed. Does NOT set
   * ``peopleTouched``: a suggestion is not a decision.
   */
  const applySuggestion = useCallback((area) => {
    const { assigneeId, participantIds } = suggestionPeople(area);
    setForm((s2) => ({
      ...s2,
      assigned_to_user_id: assigneeId,
      participant_user_ids: participantIds,
    }));
    setSuggestedFrom(area?.activity_category || null);
  }, []);

  /** RESP1: the category handler. Applies, replaces or leaves alone.
   *
   * The decision itself lives in ``resolveCategoryChange`` so it can be
   * tested without driving an async combobox; this only carries it out.
   */
  const setActivityCategory = useCallback(
    (code) => {
      setField("activity_category", code);
      if (isEdit) return;
      const area = code ? responsibilityByCategory[code] : null;

      switch (resolveCategoryChange({ area, peopleTouched, suggestedFrom })) {
        case "apply":
          applySuggestion(area);
          break;
        case "clear":
          setForm((s2) => ({
            ...s2,
            assigned_to_user_id: "",
            participant_user_ids: [],
          }));
          setSuggestedFrom(null);
          break;
        default:
          // The user decided, or there was nothing to withdraw. Either
          // way the people fields stay exactly as they are.
          setSuggestedFrom(null);
      }
    },
    [isEdit, responsibilityByCategory, peopleTouched, suggestedFrom, applySuggestion]
  );

  /** The area whose defaults the user could still adopt by hand. */
  const offerableArea =
    !isEdit && peopleTouched && form.activity_category
      ? responsibilityByCategory[form.activity_category]
      : null;
  const canOfferSuggestion = hasUsableDefaults(offerableArea);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Titel ist erforderlich.");
      return;
    }
    if (!isEdit && !form.activity_category) {
      toast.error("Tätigkeitsbereich ist erforderlich.");
      return;
    }
    // Time-range pair check (mirrors backend Pydantic validator).
    const hasStart = !!form.start_at && !form.all_day;
    const hasEnd = !!form.end_at && !form.all_day;
    if (hasStart !== hasEnd) {
      toast.error("Start und Ende müssen beide gesetzt oder beide leer sein.");
      return;
    }
    if (hasStart && hasEnd) {
      const s = new Date(form.start_at);
      const eD = new Date(form.end_at);
      if (eD < s) {
        toast.error("Ende darf nicht vor Start liegen.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const payload = formToPayload(form);
      if (isEdit) {
        await updateAdminTask(task.id, payload);
        toast.success("Aufgabe aktualisiert.");
      } else {
        await createAdminTask(payload);
        toast.success("Aufgabe erstellt.");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      // RESP2d: a category retired while this form was open needs the
      // catalog refetched, not just a message — otherwise the picker
      // goes on offering the option that just failed.
      const categoryMessage = activityCategoryErrorMessage(err);
      if (categoryMessage) {
        void invalidateActivityCategoryCatalog();
        toast.error(categoryMessage);
      } else {
        toast.error(err?.message || "Speichern fehlgeschlagen.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  // Slice A: semantic theme tokens only — the modal shell (DialogContent,
  // bg-background) follows the document theme, so hardcoded dark hex on the
  // fields turned the form into dark islands on a light page. Mirrors
  // WorkReportFormModal's field styles, which were token-based from the start.
  const fieldLabel = "mb-1 block text-[11px] font-medium text-muted-foreground";
  const fieldInput =
    "block w-full rounded-[8px] border border-input bg-background px-3 py-2 text-[13px] text-foreground outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50";

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[90vh] max-w-[640px] flex-col">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Aufgabe bearbeiten" : "Neue Aufgabe"}
          </DialogTitle>
          <DialogDescription>
            Titel ist Pflicht. Zeitfenster (Start/Ende) sind optional —
            wenn gesetzt, müssen beide ausgefüllt sein.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit}
          className="flex min-h-0 flex-1 flex-col"
        >
          <div className="grid flex-1 gap-3 overflow-y-auto pr-1">
          <div>
            <label htmlFor="task-title" className={fieldLabel}>
              Titel
            </label>
            <input
              id="task-title"
              type="text"
              value={form.title}
              onChange={(e) => setField("title", e.target.value)}
              maxLength={200}
              required
              className={fieldInput}
              placeholder="z. B. Wohnung mit Bewerber besichtigen"
            />
          </div>

          <div>
            <label htmlFor="task-description" className={fieldLabel}>
              Beschreibung (optional)
            </label>
            <textarea
              id="task-description"
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              rows={3}
              className={fieldInput}
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="task-type" className={fieldLabel}>
                Typ
              </label>
              <select
                id="task-type"
                value={form.task_type}
                onChange={(e) => setField("task_type", e.target.value)}
                className={fieldInput}
              >
                {TASK_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="task-priority" className={fieldLabel}>
                Priorität
              </label>
              <select
                id="task-priority"
                value={form.priority}
                onChange={(e) => setField("priority", e.target.value)}
                className={fieldInput}
              >
                {PRIORITY_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="task-activity-category" className={fieldLabel}>
              Tätigkeitsbereich{isEdit ? " (optional)" : ""}
            </label>
            {/* RESP2d: the options are the organisation's OWN areas —
                its custom ones included, under whatever it has renamed
                them to. A retired area may be kept but not chosen, in
                edit mode as well as create; see ActivityCategorySelect
                for why that asymmetry is deliberate. */}
            <ActivityCategorySelect
              id="task-activity-category"
              value={form.activity_category}
              onChange={setActivityCategory}
              className={fieldInput}
            />
          </div>

          <div>
            <label htmlFor="task-due-at" className={fieldLabel}>
              Fällig am
            </label>
            <input
              id="task-due-at"
              type="datetime-local"
              value={form.due_at}
              onChange={(e) => setField("due_at", e.target.value)}
              className={fieldInput}
            />
          </div>

          <div className="flex items-center gap-2">
            <input
              id="task-all-day"
              type="checkbox"
              checked={form.all_day}
              onChange={(e) => setField("all_day", e.target.checked)}
              className="h-4 w-4 rounded border-input bg-background accent-primary focus-visible:ring-1 focus-visible:ring-ring"
            />
            <label
              htmlFor="task-all-day"
              className="text-[12px] font-medium text-foreground"
            >
              Ganztägig (kein Zeitfenster)
            </label>
          </div>

          {!form.all_day ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label htmlFor="task-start-at" className={fieldLabel}>
                  Start
                </label>
                <input
                  id="task-start-at"
                  type="datetime-local"
                  value={form.start_at}
                  onChange={(e) => setField("start_at", e.target.value)}
                  className={fieldInput}
                />
              </div>
              <div>
                <label htmlFor="task-end-at" className={fieldLabel}>
                  Ende
                </label>
                <input
                  id="task-end-at"
                  type="datetime-local"
                  value={form.end_at}
                  onChange={(e) => setField("end_at", e.target.value)}
                  className={fieldInput}
                />
              </div>
            </div>
          ) : null}

          {/* PICK1c: was a native <select> over the WHOLE org, handed
              down as the `users` prop. It had no search, cost a full
              user list on every page that mounts this modal, and was
              simply EMPTY for a manager — /api/admin/users is admin-only
              and the 403 was swallowed. The picker searches the server
              one page at a time and names an already-assigned person by
              id, even a deactivated one. */}
          <div>
            <UserPicker
              label="Zuständig"
              value={form.assigned_to_user_id}
              onChange={setAssignee}
            />
          </div>

          {/* ASSIGN1: the people helping. Below "Zuständig" because it
              reads as an addition to it, and above the entity links
              because both fields are about PEOPLE and splitting them
              would put a room picker between two colleagues.

              ``selectedItems`` comes from the detail response, which
              ships each participant with its label — so the chips are
              named on first paint and the picker never spends a request
              per selected id. ``excludeIds`` keeps the responsible
              person out of the options; ``setAssignee`` above handles
              the other direction. */}
          <div>
            <UserMultiPicker
              value={form.participant_user_ids}
              onChange={(next) => setIds("participant_user_ids", next)}
              selectedItems={isEdit ? task?.participants || [] : []}
              excludeIds={[form.assigned_to_user_id]}
              disabled={submitting}
            />
          </div>

          {/* RESP1: says where these names came from, and never claims
              they are binding — the field above stays fully editable. */}
          {suggestedFrom && !isEdit ? (
            <p
              className="m-0 text-[11px] text-muted-foreground"
              data-testid="task-responsibility-hint"
            >
              Standardzuständigkeit vorgeschlagen — jederzeit änderbar.
            </p>
          ) : null}

          {/* Offered only once the user has decided something, because
              that is exactly when we must NOT act on our own. Adopting
              is a deliberate click. */}
          {canOfferSuggestion ? (
            <button
              type="button"
              onClick={() => applySuggestion(offerableArea)}
              className="rounded-[8px] border border-border bg-transparent px-2 py-1 text-[12px] font-medium text-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              data-testid="task-adopt-responsibility"
            >
              Standard übernehmen
            </button>
          ) : null}

          <div className="rounded-[8px] border border-border bg-muted/40 p-3">
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted-foreground">
              Verknüpfungen (optional)
            </p>
            {/* ME3a: several links per type. The D4b property/unit/room
                grouping is gone with the single-value pickers — with sets
                there is no "the" room to keep consistent with "the" unit,
                and the backend owns that check anyway. */}
            <MultiEntityLinkFields
              value={form}
              onChange={setIds}
              row={isEdit ? task : null}
              disabled={submitting}
            />
          </div>

          </div>
          <DialogFooter className="shrink-0 pt-3">
            <button
              type="button"
              onClick={() => handleClose(false)}
              disabled={submitting}
              className="rounded-[8px] border border-border bg-transparent px-4 py-2 text-[13px] font-semibold text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
            >
              Abbrechen
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="rounded-[8px] bg-primary px-4 py-2 text-[13px] font-semibold text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:opacity-50"
            >
              {submitting
                ? "Speichert …"
                : isEdit
                ? "Speichern"
                : "Erstellen"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
